import { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { LocalStorage } from '@va/util/helpers';
import { StorageKeysManager } from '@va/util/misc';
import { useCallback, useEffect, useMemo, useState } from 'react';

type UseColumnVisibilityParams = {
  namespace: string;
  reportBlockId: string;
  selectedView: string;
};

function getStorageKey(namespace: string, reportBlockId: string, selectedView: string) {
  return StorageKeysManager.getColumnVisibilityKey({ namespace, reportBlockId, view: selectedView });
}

function loadFromStorage(key: string): VisibilityState {
  try {
    const stored = LocalStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch (error) {
    console.error('Error while parsing column visibility from storage:', error);
  }
  return {};
}

function saveToStorage(key: string, state: VisibilityState) {
  try {
    LocalStorage.setItem(key, JSON.stringify(state));
  } catch (error) {
    console.error('Error while saving column visibility to storage:', error);
  }
}

export const useColumnVisibility = ({ namespace, reportBlockId, selectedView }: UseColumnVisibilityParams) => {
  const storageKey = useMemo(
    () => getStorageKey(namespace, reportBlockId, selectedView),
    [namespace, reportBlockId, selectedView],
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => loadFromStorage(storageKey));

  useEffect(() => {
    setColumnVisibility(loadFromStorage(storageKey));
  }, [storageKey]);

  const onColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
    (updater) => {
      setColumnVisibility((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        saveToStorage(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const isNonDefault = useMemo(() => Object.values(columnVisibility).some((v) => v === false), [columnVisibility]);

  const resetToDefault = useCallback(() => {
    setColumnVisibility({});
    saveToStorage(storageKey, {});
  }, [storageKey]);

  return {
    columnVisibility,
    onColumnVisibilityChange,
    isNonDefault,
    resetToDefault,
  };
};
